import type {
  ComboboxProps as AriakitComboboxProps,
  ComboboxItemProps,
  ComboboxPopoverProps,
} from '@ariakit/react';
import {
  Combobox as AriakitCombobox,
  ComboboxItem,
  ComboboxPopover,
  ComboboxProvider,
  useComboboxStore,
  useStoreState,
} from '@ariakit/react';
import type { Placement } from '@floating-ui/react';
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FieldInputProps } from 'formik';
import type {
  ComponentPropsWithoutRef,
  CSSProperties,
  FC,
  FocusEvent,
  ReactNode,
} from 'react';
import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { z } from 'zod';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { useFormMode } from 'src/components/form/form-mode-context';
import type { FormOption } from 'src/components/form/form-option';
import { InnerAddon } from 'src/components/form/inner-addon';
import { getInputClasses } from 'src/components/form/input';
import { FieldLabel } from 'src/components/form/label';
import { RemoveButton } from 'src/components/form/select/components/remove-button';
import { SelectToggleIcon } from 'src/components/form/select/components/select-toggle';
import { Separator } from 'src/components/form/select/components/separator';
import type { SortByOptions } from 'src/components/form/select/utils';
import {
  applySortBy,
  selectOptionBaseClasses,
} from 'src/components/form/select/utils';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { requiredOutput } from 'src/components/form/zod-utilities';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useAngular } from 'src/hooks/use-angular';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import type { RectReadOnly } from 'src/hooks/use-measure';
import { useOnChange } from 'src/hooks/use-on-change';
import { flyoutClasses } from 'src/styles';
import { cn } from 'src/utils/cn';

/**
 * Use the base schema if you add more fields to the option.
 */
export const searchableSingleSelectBaseSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const nullableSearchableSingleSelectSchema =
  searchableSingleSelectBaseSchema.nullable();

export const searchableSingleSelectSchema =
  nullableSearchableSingleSelectSchema.transform(
    requiredOutput({ message: 'Please select a value for {label}.' })
  );

type SearchableSelectValue<Value extends string> = FormOption<Value> | null;

export type SearchableSingleSelectProps<Value extends string = string> = {
  stacked?: boolean;
  inline?: boolean;
  /**
   * If you pass undefined, this means that the initial list of options is still loading.
   */
  options: FormOption<Value>[] | undefined;
  /**
   * When you use server side search (or maybe a debounced client side search) the options you
   * see don't necessarily reflect the current search value as the result is asynchronous and you could
   * have changed the search value in the meantime. This prop is used to keep track of the search value
   * that belongs to the current options.
   * (For a non-debounced client side search this _always_ equals the current search value.)
   *
   * `undefined` means there was no search yet.
   * `null` (or empty string) means we got a search result, but haven't used a search value.
   */
  searchValueForCurrentOptions: string | null | undefined;
  onChange?: (value: Value | null) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement, Element>) => void;
  placeholder?: string;
  /**
   * Defaults to 'manual' which makes most sense for server side search.
   */
  sortBy?: SortByOptions;
  /**
   * The search value always reflects the input value of our search field.
   */
  searchValue: string;
  setSearchValue: (value: string) => void;
  initialOpen?: boolean;
  leftAddon?: ReactNode;
  popoverProps?: (
    props: ComboboxPopoverProps<'div'>
  ) => ComboboxPopoverProps<'div'>;
  renderOptions?: (props: { children: ReactNode }) => ReactNode;
  pending: boolean;
  placement?: Placement;
  className?: string;
  /**
   * It is advertised to always show the chevron to ensure the element can be identified as a select.
   * Only hide it if this was discussed on a higher level.
   */
  hideChevron?: boolean;
} & FormFieldProps;

/**
 * Use this if you have a long list of options where you want to search within and where the user can only select one.
 *
 * If you have a short list of options (e.g. 2-5), consider using the [Radios](../?path=/docs/components-form-radios--docs) component.
 *
 * If you have a long list of options and don't need search, consider using the [Single Select](../?path=/docs/components-form-single-select--docs) component.
 */
export function SearchableSingleSelect<Value extends string = string>(
  props: SearchableSingleSelectProps<Value>
) {
  const { isStacked, minTablet } = useDefaultStacked();

  const {
    name,
    label,
    pending = false,
    placeholder = 'Type to Search',
    stacked = isStacked,
    inline,
    searchValueForCurrentOptions,
    hideLabel = false,
    options,
    searchValue,
    setSearchValue,
    disabled,
    disabledMessage,
    initialOpen,
    hint,
    info,
    leftAddon,
    onChange,
    onFocus,
    popoverProps,
    renderOptions,
    sortBy,
    markAsRequired,
    placement,
    className,
    hideChevron,
  } = props;

  const mode = useFormMode();
  const keepSearchValue = mode === 'search';

  const [field, showFieldError, fieldError, helper] =
    useCustomField<SearchableSelectValue<Value> | null>(name, label);

  // our actual `field` is a complex object (`FormOption`), but for validation errors
  // we're usually only interested in `FormOption['value']` and "prefer" those.
  const [_, showValueError, valueError] = useCustomField<Value | undefined>(
    `${name}.value`,
    label
  );

  const error = valueError ?? fieldError;
  const showError = showFieldError || showValueError;

  // we don't care about this. we just don't want unhandled `defined` validation errors to pop up for labels
  // as they aren't important
  useCustomField<string | undefined>(`${name}.label`, label);

  // in general we want to update the selected key and search value
  // whenever the field value changes, but we ignore the very first
  // field value in case there is an initial search value set (but no initial
  // value -> see next useEffect)
  useOnChange(() => {
    if (keepSearchValue) return;
    setSearchValue(field.value?.label ?? '');
  }, field.value?.value);

  // whenever an initial value was set, we update the search value so
  // it reflects the initial label (but only if there is no initial search value set)
  useLayoutEffect(() => {
    if (!field.value) return;
    if (searchValue) return;
    setSearchValue(field.value.label);
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  const formGroup = useFieldGroup();

  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const id = useId();

  const fieldId = formGroup?.id || id;

  const fieldItem = (
    <FieldItem>
      <Tooltip
        content={disabled && disabledMessage ? disabledMessage : undefined}
      >
        {(targetProps) => (
          <Combobox<Value>
            initialOpen={initialOpen}
            pending={pending}
            name={name}
            placeholder={placeholder}
            options={options}
            id={fieldId}
            label={label}
            searchValueForCurrentOptions={searchValueForCurrentOptions}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            showError={showError}
            field={field}
            disabled={disabled}
            onFocus={onFocus}
            leftAddon={leftAddon}
            popoverProps={popoverProps}
            renderOptions={renderOptions}
            sortBy={sortBy}
            placement={placement}
            selectedValue={field.value?.value ?? ''}
            setSelectedValue={(value) => {
              if (!value) {
                helper.setValue(null);
                onChange?.(null);
                setSearchValue('');
                return;
              }

              const newItem = options?.find((item) => item.value === value);

              if (!newItem)
                // this should never happen
                throw new Error('The selected option could not be found.');

              helper.setValue(newItem);
              onChange?.(newItem.value);
              if (keepSearchValue) return;
              setSearchValue(newItem.label);
            }}
            className={className}
            hideChevron={hideChevron}
            {...targetProps}
          />
        )}
      </Tooltip>

      {hint && <FormHint isDisabled={props.disabled}>{hint}</FormHint>}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked} inline={inline}>
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={showError}
      />

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
}

type ComboboxProps<Value extends string = string> = {
  id: string;
  pending: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  showError?: boolean;
  /**
   * If you pass undefined, this means that the initial list of options is still loading.
   */
  options: FormOption<Value>[] | undefined;
  /**
   * When you use server side search (or maybe a debounced client side search) the options you
   * see don't necessarily reflect the current search value as the result is asynchronous and you could
   * have changed the search value in the meantime. This prop is used to keep track of the search value
   * that belongs to the current options.
   * (For a non-debounced client side search this _always_ equals the current search value.)
   *
   * `undefined` means there was no search yet.
   * `null` (or empty string) means we got a search result, but haven't used a search value.
   */
  searchValueForCurrentOptions: string | null | undefined;
  placeholder: string;
  field: FieldInputProps<SearchableSelectValue<Value> | null>;
  initialOpen?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement, Element>) => void;
  leftAddon?: ReactNode;
  popoverProps?: (
    props: ComboboxPopoverProps<'div'>
  ) => ComboboxPopoverProps<'div'>;
  renderOptions?: (props: { children: ReactNode }) => ReactNode;
  /**
   * Defaults to 'manual' which makes most sense for server side search.
   */
  sortBy?: SortByOptions;
  selectedValue: string;
  setSelectedValue: (id: string) => void;
  placement?: Placement;
  className?: string;
  /**
   * It is advertised to always show the chevron to ensure the element can be identified as a select.
   * Only hide it if this was discussed on a higher level.
   */
  hideChevron?: boolean;
} & FormFieldProps;

function Combobox<Value extends string = string>(props: ComboboxProps<Value>) {
  const {
    name,
    searchValue,
    id,
    field,
    placeholder,
    showError,
    label,
    disabled,
    pending,
    initialOpen,
    setSearchValue,
    onFocus,
    leftAddon,
    popoverProps = (props) => props,
    renderOptions = ({ children }) => children,
    sortBy = 'manual',
    options: unsortedOptions,
    selectedValue,
    setSelectedValue,
    placement,
    className,
    hideChevron,
  } = props;

  const options = applySortBy(sortBy, unsortedOptions ?? []);

  const focusRef = useFocusManager<HTMLInputElement>();

  const showRemoveButton = Boolean(selectedValue);

  // in case the input just got focused, we want to show an empty input,
  // but we don't want to immediately change the actual search value as this might trigger requests
  const [justGotFocus, setJustGotFocus] = useState(false);

  const store = useComboboxStore({
    selectedValue,
    setSelectedValue,
    value: justGotFocus ? '' : searchValue,
    setValue: (value) => {
      setJustGotFocus(false);
      setSearchValue(value);
    },
    defaultOpen: initialOpen,
    placement,
  });

  const isOpen = useStoreState(store, 'open');
  const activeId = useStoreState(store, 'activeId');

  const isFocused =
    document.activeElement && focusRef.current === document.activeElement;

  const selectedValueAsPlaceholder = Boolean(
    isFocused && (justGotFocus || !searchValue) && selectedValue
  );

  const popoverChildren = (
    <>
      {pending ? (
        <SearchableSingleSelectContent>
          <EmptyValue label="Loading search results..." />
        </SearchableSingleSelectContent>
      ) : (
        <>
          {!unsortedOptions && (
            <SearchableSingleSelectContent>
              <EmptyValue label="Type to search..." />
            </SearchableSingleSelectContent>
          )}

          {options.map((option) => (
            <ItemWithTooltips
              key={option.value}
              activeId={activeId}
              option={option}
              selectedValue={selectedValue}
              id={id}
              showError={showError}
              name={name}
            />
          ))}

          {unsortedOptions?.length === 0 && searchValue && (
            <SearchableSingleSelectContent>
              <EmptyValue label={`Could not find ${searchValue}.`} />
            </SearchableSingleSelectContent>
          )}

          {unsortedOptions?.length === 0 && !searchValue && (
            <SearchableSingleSelectContent>
              <EmptyValue label="No options available to search for." />
            </SearchableSingleSelectContent>
          )}
        </>
      )}
    </>
  );

  // iframes need custom interaction handling
  const angular = useAngular();
  useLayoutEffect(() => {
    const contentWindow = angular.iframe?.contentWindow;
    if (!contentWindow) return;
    if (!isOpen) return;
    const handlePointerDown = () => store.setOpen(false);
    contentWindow.addEventListener('pointerdown', handlePointerDown);
    return () => {
      contentWindow.removeEventListener('pointerdown', handlePointerDown);
    };
    // oxlint-disable-next-line exhaustive-deps
  }, [isOpen, angular.iframe]);

  return (
    <div>
      {/*
         keep hidden input for compatibility with regular single select,
         so you can easily switch to the component without changing test code
       */}
      <input
        type="hidden"
        data-testid={name}
        value={field.value?.value ?? ''}
      />

      <ComboboxProvider store={store}>
        <InnerAddon
          left={leftAddon}
          right={
            <div
              className={`
                grid grid-flow-col items-center gap-xs
                legacy:gap-[0.5rem]
              `}
            >
              {showRemoveButton && (
                <div
                  className={`
                    grid grid-flow-col items-center gap-xs
                    legacy:gap-[0.5rem]
                  `}
                >
                  <RemoveButton
                    error={showError}
                    disabled={disabled}
                    data-testid={`${name}-remove-selection`}
                    onClick={() => {
                      setSelectedValue('');
                    }}
                    className="[pointer-events:initial]"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </RemoveButton>
                  <Separator error={showError} disabled={disabled} />
                </div>
              )}

              {!hideChevron && (
                <SelectToggleIcon
                  showError={showError}
                  disabled={disabled}
                  isOpen={isOpen}
                />
              )}
            </div>
          }
        >
          {(addonBounds) => (
            <SearchableSelectInput
              id={id}
              ref={focusRef}
              error={showError}
              disabled={disabled}
              data-name={name} // for focus manager
              data-testid={`${name}Search`}
              placeholder={
                selectedValueAsPlaceholder ? field.value?.label : placeholder
              }
              selectedValueAsPlaceholder={selectedValueAsPlaceholder}
              {...addonBounds}
              onBlur={(e) => {
                setJustGotFocus(false);
                if (searchValue === field.value?.label) return;
                if (!field.value) return setSearchValue('');
                setSearchValue(field.value.label);
              }}
              onFocus={(e) => {
                setJustGotFocus(true);
                onFocus?.(e);
              }}
              className={cn(
                className,
                isFocused && searchValue !== field.value?.label && 'italic'
              )}
            />
          )}
        </InnerAddon>

        {createPortal(
          <ComboboxPopover
            {...popoverProps({
              className: comboboxPopoverClasses,
              'aria-busy': pending,
              'data-testid': `${name}-options`,
              unmountOnHide: true,
            })}
          >
            {renderOptions({ children: popoverChildren })}
          </ComboboxPopover>,
          document.body
        )}
      </ComboboxProvider>
    </div>
  );
}

export const SearchableSelectInput = forwardRef<
  HTMLInputElement,
  AriakitComboboxProps & {
    error?: boolean;
    leftBounds?: RectReadOnly;
    rightBounds?: RectReadOnly;
    selectedValueAsPlaceholder?: boolean;
  }
>(
  (
    { error, leftBounds, rightBounds, selectedValueAsPlaceholder, ...props },
    ref
  ) => (
    <AriakitCombobox
      ref={ref}
      {...props}
      style={
        {
          '--field-padding-left': leftBounds
            ? `${leftBounds.width}px`
            : undefined,
          '--field-padding-right': rightBounds
            ? `${rightBounds.width}px`
            : undefined,
          ...props.style,
        } as CSSProperties
      }
      className={getInputClasses({
        error,
        className: cn(
          '[pointer-events:initial]!', // https://github.com/ariakit/ariakit/issues/845#issuecomment-785493623,
          leftBounds &&
            `
              pl-(--field-padding-left)
              legacy:pl-(--field-padding-left)
            `,
          rightBounds &&
            `
              pr-(--field-padding-right)
              legacy:pr-(--field-padding-right)
            `,
          selectedValueAsPlaceholder &&
            `
              placeholder:text-foreground-surface-default placeholder:not-italic
              placeholder:opacity-100
              legacy:placeholder:text-legacy-brand
            `,
          props.className
        ),
      })}
    />
  )
);

export const comboboxPopoverClasses = cn(
  flyoutClasses,
  `
    max-h-[23.5rem] w-(--popover-anchor-width) max-w-(--popover-available-width)
    min-w-[17.9rem] overflow-auto
  `
);

export const SearchableSingleSelectContent: FC<
  ComponentPropsWithoutRef<'div'>
> = (props) => (
  <div {...props} className={cn('px-xl py-md', props.className)} />
);

function ItemWithTooltips<Value extends string = string>({
  id,
  activeId,
  option,
  selectedValue,
  name,
}: {
  id?: string;
  activeId: string | null | undefined;
  option: FormOption<Value>;
  selectedValue: Value;
  showError?: boolean;
  name: string;
}) {
  const infoRef = useRef<HTMLElement>(null);
  const itemTestId = `${name}-option-${option.value}`;
  const itemId = `${id}-${itemTestId}`;

  const active = activeId === itemId;

  return (
    <Tooltip
      key={option.value}
      content={option.disabled && option.disabledMessage}
      noTabIndex
      controlOpen={active}
    >
      {(disabledMessageTargetProps) => (
        <Tooltip
          content={option.info}
          positionRef={infoRef}
          noTabIndex
          controlOpen={active}
        >
          {(infoTargetProps) => (
            <SearchableSingleSelectItem
              {...infoTargetProps}
              {...disabledMessageTargetProps}
              disabled={option.disabled}
              value={option.value}
              accessibleWhenDisabled // make it possible to discover disabled items via keyboard
              id={itemId}
              data-testid={itemTestId}
              // it looks like for a real combobox `aria-selected` is not recommended,
              // therefor ariakit doesn't set it. we use our own attribute here
              // for styling purposes only.
              data-style-is-selected={option.value === selectedValue}
            >
              <div
                className={`
                  grid grid-flow-col items-baseline justify-between gap-xl
                  legacy:gap-[2rem]
                `}
              >
                <span>
                  {option.label}
                  {option.info && (
                    <>
                      {' '}
                      <FontAwesomeIcon
                        forwardedRef={infoRef}
                        icon={faInfoCircle}
                        className={cn(
                          option.value === selectedValue
                            ? `text-white`
                            : `
                              text-foreground-primary-inverted
                              legacy:text-legacy-brand
                            `
                        )}
                      />
                    </>
                  )}
                </span>

                {option.value === selectedValue && (
                  <FontAwesomeIcon
                    data-testid={`${name}-option-${option.value}-isSelected`}
                    icon={faCheck}
                  />
                )}
              </div>

              {option.hint && (
                <FormHint
                  isDisabled={option.value !== selectedValue && option.disabled}
                  isSelected={option.value === selectedValue}
                >
                  {option.hint}
                </FormHint>
              )}
            </SearchableSingleSelectItem>
          )}
        </Tooltip>
      )}
    </Tooltip>
  );
}

export const SearchableSingleSelectItem: FC<ComboboxItemProps> = (props) => {
  return (
    <ComboboxItem
      focusOnHover
      setValueOnClick={false}
      resetValueOnSelect={false}
      hideOnClick={false}
      {...props}
      className={cn(
        selectOptionBaseClasses,
        `
          data-[style-is-selected='true']:bg-background-surface-selected
          data-[style-is-selected='true']:text-foreground-surface-selected
          data-[style-is-selected='true']:focus-visible:contrast-focus-style
          [[data-active-item]:not([data-style-is-selected='true'],[aria-disabled])]:bg-background-surface-hover
          [[data-active-item]:not([data-style-is-selected='true'],[aria-disabled])]:text-foreground-surface-hover
        `,
        `
          legacy:data-[style-is-selected='true']:bg-legacy-brand-light-2
          legacy:data-[style-is-selected='true']:text-white
          legacy:[[data-active-item]:not([data-style-is-selected='true'],[aria-disabled])]:bg-legacy-background
          legacy:[[data-active-item]:not([data-style-is-selected='true'],[aria-disabled])]:text-legacy-brand
        `,
        props.className
      )}
    />
  );
};
